/**
 * Per-viewer audio delay controller built on the Web Audio DelayNode graph.
 *
 * Graph: AudioContext -> createMediaElementSource(videoEl) -> createDelay(1)
 *        -> destination.
 *
 * Non-negotiable requirements (master build prompt §WS-7 + §9):
 *  - The graph is built lazily on first user interaction and the context is
 *    resumed (a context created before a gesture starts suspended).
 *  - The graph MUST connect to destination, or the video goes silent the
 *    moment createMediaElementSource re-routes the element's audio.
 *  - createMediaElementSource is called exactly ONCE per element and stored;
 *    calling it twice on the same element throws InvalidStateError.
 *  - Positive-only (0..AUDIO_DELAY_MAX_MS). DelayNode.delayTime min is 0, so
 *    audio can only be delayed, never advanced.
 *  - isAvailable() is false when AudioContext is missing or a cross-origin /
 *    CORS failure would silence the media. We serve same-origin, so this should
 *    not fire, but we degrade cleanly rather than muting the video.
 *  - dispose() disconnects the nodes and closes the context.
 *
 * @packageDocumentation
 */
import { LIMITS } from '@syncreel/shared';

/** The controller surface exported for the composition root (§5.3). */
export interface AudioDelayController {
  /** Set the delay in milliseconds (clamped to 0..AUDIO_DELAY_MAX_MS). */
  setDelayMs(ms: number): Promise<void>;
  /** Read the current delay in milliseconds. */
  getDelayMs(): number;
  /** Tear down the audio graph and close the context. */
  dispose(): void;
  /** Whether the graph can be built on this browser/media combination. */
  isAvailable(): boolean;
}

/**
 * Max delay supported by the DelayNode, in seconds.
 * WHY: createDelay(maxDelayTime) sets the ceiling for delayTime.value; we
 * allocate enough headroom to cover LIMITS.AUDIO_DELAY_MAX_MS (60 s). Browsers
 * allow large values here (the Web Audio spec permits up to ~180 s).
 */
const DELAY_NODE_MAX_S = 60;

/**
 * Resolve the AudioContext constructor across vendor prefixes without `any`.
 */
function getAudioContextCtor(): typeof AudioContext | undefined {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

/**
 * True when HLS is played by the browser's native stack (iPhone Safari has no
 * MediaSource, so video.js cannot take over). WebKit feeds Web Audio silence
 * (zeros) for native HLS (WebKit bug 180696, still open), so routing the
 * element through a graph would mute the movie. Treat the delay as unavailable.
 */
function isNativeHlsPlayback(): boolean {
  return typeof MediaSource === 'undefined';
}

/**
 * Create an audio delay controller for a single video element.
 *
 * The Web Audio graph is constructed on the first `setDelayMs` call (which is
 * invoked from a user gesture on the slider) so the context can be resumed.
 */
export function createAudioDelayController(
  videoEl: HTMLVideoElement,
): AudioDelayController {
  let audioContext: AudioContext | null = null;
  let sourceNode: MediaElementAudioSourceNode | null = null;
  let delayNode: DelayNode | null = null;
  let currentDelayMs = 0;
  let corsFailed = false;
  let disposed = false;

  /**
   * Pre-flight CORS check. createMediaElementSource re-routes element audio
   * into the graph; if the media is cross-origin without proper CORS, the
   * output is silenced with no error event. We serve same-origin, so this is a
   * defensive degradation only.
   */
  function wouldCorsFail(): boolean {
    try {
      const src = videoEl.currentSrc || videoEl.src;
      if (!src) return false;
      const url = new URL(src, window.location.href);
      if (url.origin !== window.location.origin) {
        // WHY: cross-origin media requires crossOrigin='anonymous' AND
        // Access-Control-Allow-Origin on the response. We cannot verify
        // response headers without a fetch, so flag untagged cross-origin as
        // unavailable to avoid silently muting the video.
        return videoEl.crossOrigin !== 'anonymous';
      }
    } catch {
      // Invalid/unparseable URL — treat as same-origin (no failure).
    }
    return false;
  }

  /** Lazily build the audio graph exactly once. */
  function ensureGraph(): void {
    if (audioContext || disposed) return;
    const Ctor = getAudioContextCtor();
    if (!Ctor) return; // No Web Audio -> stays unavailable.
    if (isNativeHlsPlayback()) return;
    if (wouldCorsFail()) {
      corsFailed = true;
      return;
    }
    const ctx = new Ctor();
    // WHY: createMediaElementSource re-routes the element's audio into the
    // graph. It must be created exactly once per element, ever; a second call
    // throws InvalidStateError. Store it for the controller's lifetime.
    sourceNode = ctx.createMediaElementSource(videoEl);
    // WHY: createDelay(DELAY_NODE_MAX_S) allocates a max delayTime of 60 s,
    // matching LIMITS.AUDIO_DELAY_MAX_MS. @see https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/createDelay
    delayNode = ctx.createDelay(DELAY_NODE_MAX_S);
    delayNode.delayTime.value = 0;
    sourceNode.connect(delayNode);
    // WHY: the graph must connect to destination or the video goes silent once
    // the source node is created (element audio is re-routed through it).
    delayNode.connect(ctx.destination);
    audioContext = ctx;
  }

  return {
    async setDelayMs(ms: number): Promise<void> {
      if (disposed) return;
      const clamped = Math.max(0, Math.min(LIMITS.AUDIO_DELAY_MAX_MS, ms));
      currentDelayMs = clamped;

      ensureGraph();
      if (!audioContext || !delayNode) return;

      // WHY: a context created before a user gesture starts suspended.
      // setDelayMs is invoked from the slider's input handler (a user
      // gesture), so resume() is allowed and starts audio flowing.
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch {
          // Resume may reject if the context is closing; ignore and leave the
          // delay value set for when audio does flow.
        }
      }
      delayNode.delayTime.value = clamped / 1000;
    },

    getDelayMs(): number {
      return currentDelayMs;
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      try {
        delayNode?.disconnect();
      } catch {
        // Node may already be disconnected; ignore.
      }
      try {
        sourceNode?.disconnect();
      } catch {
        // Node may already be disconnected; ignore.
      }
      if (audioContext) {
        try {
          void audioContext.close();
        } catch {
          // Context may already be closed; ignore.
        }
      }
      delayNode = null;
      sourceNode = null;
      audioContext = null;
    },

    isAvailable(): boolean {
      if (disposed) return false;
      if (!getAudioContextCtor()) return false;
      if (isNativeHlsPlayback()) return false;
      if (corsFailed) return false;
      // Re-evaluate the CORS pre-flight if the graph has not been built yet
      // (the source URL may have settled since construction).
      if (!audioContext && wouldCorsFail()) return false;
      return true;
    },
  };
}
